import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { environment } from '@environments/environment'
import { HttpClient } from '@angular/common/http'
import { finalize, map } from 'rxjs/operators'

import cloneDeep from 'lodash-es/cloneDeep'

import { RequestType } from '@shared/models/request-type.model'
import { FilterValues } from '@shared/models/filter-values.model'
import { LoadingFileService } from './loading-file.service'
import { domainName, goSubdomainByEnv, subdomainByEnv } from '@core/helpers/domain-name'
import { InititializerService } from '@app/core/services/inititializer.service'

@Injectable({ providedIn: 'root' })
export class ApiService {
  private protocol = 'https'
  private protocolWS = 'wss'
  private _baseUrl: string
  private _baseUrlWS: string
  private _baseUrlChat: string
  private _goBaseUrl: string
  private _goBaseUrlWS: string
  private _fileUrl: string
  private _goFileUrl: string
  private _host: string

  get isProduction(): boolean {
    return process.env.NODE_ENV !== 'development'
  }

  get baseUrl(): string {
    return this._baseUrl
  }

  get host(): string {
    return this._host
  }

  get fileUrl(): string {
    return environment.apiMode === 'go' ? this._goFileUrl : this._fileUrl
  }

  get baseUrlWS(): string {
    return this._baseUrlWS
  }

  get baseUrlChat(): string {
    return this._baseUrlChat
  }

  get goBaseUrl(): string {
    return this._goBaseUrl
  }

  get goBaseUrlWS(): string {
    return this._goBaseUrlWS
  }

  constructor(
    private http: HttpClient,
    private loadingFileService: LoadingFileService,
    private initializerService: InititializerService,
  ) {}

  init() {
    const platformSettings = this.initializerService.platformSettings
    const { hostApi, hostMessenger, hostTrack } = platformSettings

    const apiSubdomain = subdomainByEnv('apiSubdomain')
    const wsSubdomain = subdomainByEnv('wsSubdomain')
    const chatSubdomain = subdomainByEnv('chatSubdomain')
    this._host = this.isProduction ? domainName() : domainName(environment.url)

    const apiDomain = hostApi || apiSubdomain + this._host
    const wsDomain = hostTrack || `${wsSubdomain}${this.isProduction ? domainName() : domainName(environment.url)}:446`
    const chatDomain = hostMessenger || `${chatSubdomain}${this.isProduction ? domainName() : domainName(environment.url)}`

    this._baseUrl = `${this.protocol}://${apiDomain}/api`
    this._fileUrl = `${this.protocol}://${apiDomain}/`
    this._baseUrlWS = `${this.protocolWS}://${wsDomain}`
    this._baseUrlChat = `${this.protocolWS}://${chatDomain}`

    const goSubdomain = goSubdomainByEnv()
    const goDomain = platformSettings.hostGoApi || goSubdomain + this._host
    this._goBaseUrl = `${this.protocol}://${goDomain}/api/v2`
    this._goBaseUrlWS = `${this.protocolWS}://${goDomain}`
    this._goFileUrl = `${this.protocol}://${goDomain}/`

    environment.fileUrl = this.fileUrl
  }

  /* eslint-disable */

  // NOTE: the POST branch (requestType=Post) is a fetch-as-POST — used when filter/field params
  // are too large for a query string. Do NOT opt in to FormFeedbackInterceptor (withFeedback):
  // this loads paginated tables and would false-toast on every page change / filter / sort.
  getPageable<T>(
    path: string,
    pageIndex = 0,
    pageSize = 10,
    sort?: string,
    filterValues?: FilterValues,
    fields?: (keyof T | string)[],
    requestType: RequestType = RequestType.Get,
  ) {
    const isGETRequest = requestType === RequestType.Get

    const filter = this.prepareFilterValuesForSending(filterValues, isGETRequest)
    const fieldsKey = isGETRequest ? 'fields[]' : 'fields'
    const params = {
      limit: pageSize,
      page: pageIndex,
      ...(sort && { sort }),
      ...filter,
      ...(fields && { [fieldsKey]: fields }),
    }

    const request = isGETRequest ? this.get(path, { params }) : this.post(path, { ...params })

    return request.pipe(
      map((response) => {
        const { data, limit, page, total, ...rest } = response as any

        return {
          data: <T[]>response['data'],
          meta: {
            limit: <number>response['limit'],
            page: <number>response['page'],
            total: <number>response['total'],
          },
          ...rest,
        }
      }),
    )
  }

  // NOTE: the POST branch (requestType=Post) is a fetch-as-POST — downloads a CSV/PDF blob.
  // Do NOT opt in to FormFeedbackInterceptor (withFeedback): a "Saved successfully" toast is
  // meaningless for a file export.
  exportFile(
    path: string,
    columns: string[],
    sort: string,
    filterValues: FilterValues,
    requestType: RequestType = RequestType.Get,
  ): Observable<string> {
    const isGETRequest = requestType === RequestType.Get
    const filter = this.prepareFilterValuesForSending(filterValues, isGETRequest)
    const fieldsKey = isGETRequest ? 'fields[]' : 'fields'
    const responseType = path.includes('/pdf') ? 'blob' : 'text'

    const params = {
      ...(sort && { sort }),
      [fieldsKey]: columns,
      ...filter,
    }
    const request$ = isGETRequest ? this.get<string>(path, { params, responseType }) : this.post<string>(path, params, { responseType })
    path.includes('/pdf') ? this.loadingFileService.setPDFLoading() : this.loadingFileService.setCSVLoading()

    return request$.pipe(finalize(() => this.loadingFileService.clearFileLoading()))
  }

  private get activeBaseUrl(): string {
    return environment.apiMode === 'go' ? this._goBaseUrl : this._baseUrl
  }

  get<T = ArrayBuffer>(path, options?: any, cleanPath?: boolean): Observable<T> {
    return this.http.get(cleanPath ? path : `${this.activeBaseUrl}${path}`, options) as any
  }

  post<T = ArrayBuffer>(path, body: any | null, options?: any): Observable<T> {
    return this.http.post(`${this.activeBaseUrl}${path}`, body, options) as any
  }

  patch<T = ArrayBuffer>(path, body: any | null, options?: any): Observable<T> {
    return this.http.patch(`${this.activeBaseUrl}${path}`, body, options) as any
  }

  delete<T = void>(path, options?: any): Observable<T> {
    return this.http.delete(`${this.activeBaseUrl}${path}`, options) as any
  }

  private prepareFilterValuesForSending(filterValues: FilterValues, isGETRequest?: boolean) {
    const filterValuesCopy = cloneDeep(filterValues) || {}

    const onlyValuable = ([, value]) => value || value === false
    const toObject = (newFilter, [key, value]) => ((newFilter[key] = value), newFilter)
    const filterRaw = Object.entries(filterValuesCopy).filter(onlyValuable).reduce(toObject, {})

    const filter = {}

    const isArray = ([, value]) => Array.isArray(value)
    const isNotEmpty = ([, value]) => value.length
    const isNotArray = (entry) => !isArray(entry)
    Object.entries(filterRaw)
      .filter(isNotArray)
      .forEach(([key, value]) => (filter[key] = typeof value === 'boolean' ? value : String(value)))

    Object.entries(filterRaw)
      .filter(isArray)
      .filter(isNotEmpty)
      .forEach(([key, value]) => {
        const filterKey = isGETRequest ? `${key}[]` : key

        filter[filterKey] = value
      })

    return filter
  }
}
